// Selecteur de scope cross-account pour la TitleBar (2026-05-27).
//
// Le user peut cliquer sur l'OrgAndStatsBadge actuel pour ouvrir un menu :
//   - Liste de toutes les orgs où il est membre (clic = scope single sur
//     cette org)
//   - Si ≥2 orgs : entrée "Toutes mes organisations" (scope = all,
//     agrégation cross-org)
//
// Le scope sélectionné est persisté dans `activeAccountScopeAtom` (cf
// core/state/shell.ts). Les panneaux qui supportent la vue "all" lisent
// l'atom et branchent leurs queries. Les panneaux legacy retombent
// silencieusement sur le 1er account.
//
// POC actuel : seul le panneau Creators consomme l'atom. Les autres
// panneaux ignorent le scope et restent en mode single. C'est une dégrada-
// tion gracieuse : l'user qui sélectionne "All" voit "All" dans Creators
// et son single account ailleurs — pas idéal mais pas cassé.

import { useQuery } from "convex/react";
import { useAtom } from "jotai";
import { Building2, ChevronDown, Globe2 } from "@/shared/ui/icons";
import { useTranslation } from "react-i18next";

import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { activeAccountScopeAtom, type AccountScope } from "@/core/state";
import { Avatar, Button, Infobulle, useMenu, type EntreeMenu } from "@/shared/ui";
import { OrgAndStatsBadge } from "./OrgAndStatsBadge";

export function AccountScopeSwitcher() {
  const { t } = useTranslation();
  const accounts = useQuery(api.accounts.listMine);
  const [scope, setScope] = useAtom(activeAccountScopeAtom);
  const { ouvrirSous, ouvert, menu } = useMenu();

  // Pas d'accounts encore (bootstrap / 0 orgs) → on rend juste le badge,
  // pas de dropdown (rien à switcher).
  if (!accounts || accounts.length === 0) {
    return <OrgAndStatsBadge />;
  }

  // Un seul account → pas de switcher non plus, juste le badge legacy.
  // Le scope reste implicitement "single" sur cet account.
  if (accounts.length === 1) {
    return <OrgAndStatsBadge />;
  }

  // ≥2 accounts → un bouton qui enveloppe le badge + chevron, et un `Menu` de la librairie.
  const isAll = scope?.kind === "all";
  const activeAccountId = scope?.kind === "single" ? scope.accountId : accounts[0]?._id;

  function selectAccount(accountId: Id<"accounts">) {
    const next: AccountScope = { kind: "single", accountId };
    setScope(next);
  }

  const entrees: EntreeMenu[] = [
    { titre: t("shell.accountScope.header") },
    { label: t("shell.accountScope.all"), icone: <Globe2 className="h-4 w-4" />, coche: isAll, onClick: () => setScope({ kind: "all" }) },
    "sep",
    ...accounts.map(
      (a): EntreeMenu => ({
        label: a.name,
        icone: <Avatar src={a.logoUrl} icone={<Building2 className="h-3 w-3" />} taille={16} />,
        coche: !isAll && a._id === activeAccountId,
        onClick: () => selectAccount(a._id),
      }),
    ),
  ];

  return (
    <>
      <Infobulle texte={t("shell.accountScope.switchAria")} cote="bas">
        <Button
          variant={ouvert ? "secondary" : "ghost"}
          size="md"
          aria-haspopup="menu"
          aria-expanded={ouvert}
          aria-label={t("shell.accountScope.switchAria")}
          onClick={(e) => ouvrirSous(e.currentTarget, entrees, { alignement: "droite" })}
          iconRight={<ChevronDown className="h-3 w-3" />}
        >
          <OrgAndStatsBadge />
        </Button>
      </Infobulle>
      {menu}
    </>
  );
}
